import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  TextInput, 
  PasswordInput, 
  Button, 
  Title, 
  Box, 
  Alert,
  Checkbox,
  Group,
  Text,
  Stack,
  Indicator
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import classes from './FloatingLabelInput.module.css';

export default function Login({ onLogin }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dbStatus, setDbStatus] = useState({ connected: false, checking: true });
  const [focusedEmail, setFocusedEmail] = useState(false);
  const [focusedPassword, setFocusedPassword] = useState(false);

  // Vérification de la connexion à la base de données au chargement
  useEffect(() => {
    const checkDatabaseConnection = async () => {
      try {
        const response = await fetch('/api/ping');
        const data = await response.json();
        setDbStatus({ 
          connected: data.success, 
          checking: false 
        });
      } catch (error) {
        setDbStatus({ 
          connected: false, 
          checking: false 
        });
      }
    };

    checkDatabaseConnection();
  }, []);

  const handleChange = (field) => (event) => {
    const value = field === 'rememberMe' ? event.currentTarget.checked : event.currentTarget.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('jwt_token', data.token);
        onLogin(data.user);
        notifications.show({
          title: 'Connexion réussie',
          message: `Bienvenue ${data.user.prenom && data.user.nom ? `${data.user.prenom} ${data.user.nom}` : data.user.email} !`,
          color: 'green'
        });
      } else {
        setError(data.message || 'Erreur de connexion');
      }
    } catch (error) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box style={{ flex: 1 }}>
        <Container size={400} my={40}>
          <Title ta="center" mb={30}>
            TMD
          </Title>
          
          <Paper withBorder shadow="md" p={30} radius="md">
            <form onSubmit={handleSubmit}>
              <TextInput
                label="Email"
                placeholder=""
                required
                classNames={classes}
                value={formData.email}
                onChange={handleChange('email')}
                onFocus={() => setFocusedEmail(true)}
                onBlur={() => setFocusedEmail(false)}
                mb="xl"
                size="md"
                autoComplete="email"
                data-floating={formData.email.trim().length !== 0 || focusedEmail || undefined}
                labelProps={{ 'data-floating': formData.email.trim().length !== 0 || focusedEmail || undefined }}
              />
              
              <PasswordInput
                label="Mot de passe"
                placeholder=""
                required
                classNames={classes}
                value={formData.password}
                onChange={handleChange('password')}
                onFocus={() => setFocusedPassword(true)}
                onBlur={() => setFocusedPassword(false)}
                mb="md"
                size="md"
                autoComplete="current-password"
                data-floating={formData.password.trim().length !== 0 || focusedPassword || undefined}
                labelProps={{ 'data-floating': formData.password.trim().length !== 0 || focusedPassword || undefined }}
              />
              
              <Group justify="space-between" mb="md">
                <Checkbox
                  label="Se souvenir de moi"
                  checked={formData.rememberMe}
                  onChange={handleChange('rememberMe')}
                />
              </Group>

              {error && (
                <Alert color="red" mb="md">
                  {error}
                </Alert>
              )}
              
              <Button 
                type="submit" 
                fullWidth 
                loading={loading}
                disabled={loading}
              >
                Se connecter
              </Button>
            </form>
          </Paper>
        </Container>
      </Box>
      
      {/* Footer */}
      <Box 
        component="footer" 
        style={{ 
          borderTop: '1px solid #e9ecef',
          backgroundColor: '#f8f9fa',
          padding: '20px 0'
        }}
      >
        <Container>
          <Stack align="center" gap="xs">
            <Text size="sm" c="dimmed">
              © 2024 React TMD - Tous droits réservés
            </Text>
            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">
                Base de données :
              </Text>
              {dbStatus.checking ? (
                <Text size="xs" c="dimmed">Vérification...</Text>
              ) : (
                <Group gap={4} align="center">
                  <Indicator 
                    color={dbStatus.connected ? 'green' : 'red'} 
                    size={8}
                  />
                  <Text 
                    size="xs" 
                    c={dbStatus.connected ? 'green' : 'red'}
                    fw={500}
                  >
                    {dbStatus.connected ? 'Connectée' : 'Déconnectée'}
                  </Text>
                </Group>
              )}
            </Group>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
} 